import * as React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:icon');
import { cva, type VariantProps } from 'class-variance-authority';
import * as LucideIcons from 'lucide-react';

import { cn } from '../../utils/cn';
import * as svgs from './svg';

type CustomIconKeyType = keyof typeof svgs;
type LucideIconKeyType = keyof typeof LucideIcons;

// Filter out non-icon exports from lucide-react
const lucideIconNames = Object.keys(LucideIcons).filter(
  key =>
    key !== 'createLucideIcon' &&
    key !== 'default' &&
    typeof LucideIcons[key as keyof typeof LucideIcons] === 'function'
) as LucideIconKeyType[];

type IconKeyType = CustomIconKeyType | LucideIconKeyType;

const iconVariants = cva('inline-flex items-center justify-center');

// Size mapping from variant to pixel values
const sizeMap = {
  sm: 16,
  default: 20,
  md: 24,
  lg: 32,
  xl: 40,
} as const;

export type IconProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof iconVariants> & {
    iconType: IconKeyType;
    sizePx?: number;
    size?: keyof typeof sizeMap;
  };

const Icon = React.forwardRef<HTMLDivElement, IconProps>(
  ({ className, size = 'default', sizePx, iconType, style, ...props }, ref) => {
    // Check if it's a custom icon first
    const isCustomIcon = iconType in svgs;
    let IconComponent: React.ComponentType<any> | null = null;

    if (isCustomIcon) {
      // Use custom SVG icon
      IconComponent = svgs[iconType as CustomIconKeyType];
    } else {
      // Use Lucide icon
      IconComponent = LucideIcons[iconType as LucideIconKeyType] as React.ComponentType<any>;
    }

    if (!IconComponent) {
      logger.error(`Icon "${iconType}" not found in custom icons or lucide-react`);
      return null;
    }

    // Use sizePx if provided, otherwise fall back to size variant mapping
    const effectiveSize = size || 'default';
    const pixelSize = sizePx ?? sizeMap[effectiveSize];

    return (
      <div
        ref={ref}
        className={cn(iconVariants({ className }))}
        style={{
          width: `${pixelSize}px`,
          height: `${pixelSize}px`,
          ...style,
        }}
        {...props}
      >
        {isCustomIcon ? (
          <IconComponent className="h-full w-full" />
        ) : (
          <IconComponent size={pixelSize} className="h-full w-full" />
        )}
      </div>
    );
  }
);
Icon.displayName = 'Icon';

export { Icon, iconVariants };

// Export available icon types (both custom and Lucide)
export const CUSTOM_ICON_TYPES = Object.keys(svgs) as CustomIconKeyType[];
export const LUCIDE_ICON_TYPES = lucideIconNames;
export const ALL_ICON_TYPES = [...CUSTOM_ICON_TYPES, ...lucideIconNames] as IconKeyType[];

// Type-safe icon checker
export const isValidIconType = (icon: string): icon is IconKeyType => {
  return icon in svgs || lucideIconNames.includes(icon as LucideIconKeyType);
};
